import Decimal from "decimal.js";
import { roundCurrency } from "@/lib/utils/math";

export interface DebtPayoffInput {
  /** Current loan balance (principal) */
  principal: number;
  /** Annual interest rate (percent) */
  annualInterestRate: number;
  /** Minimum monthly payment */
  monthlyPayment: number;
  /** Extra payment each month (optional) */
  extraPayment?: number;
}

export interface DebtPayoffResult {
  /** Month when loan is paid off (1-based, relative to start) */
  payoffMonth: number;
  /** Total interest paid over the life of the loan */
  totalInterestPaid: number;
  /** Total amount paid (principal + interest) */
  totalPaid: number;
  /** Payoff in years (e.g., 5.5) */
  payoffYears: number;
  /** Chart data: month index and remaining balance */
  chartData: { month: number; balance: number }[];
  /** If extra payment provided: comparison without extra */
  withoutExtra?: {
    payoffMonth: number;
    totalInterestPaid: number;
    payoffYears: number;
  };
}

/**
 * Simulate debt payoff month by month. Payment goes to interest first, then principal.
 */
export function calculateDebtPayoff(input: DebtPayoffInput): DebtPayoffResult {
  const {
    principal,
    annualInterestRate,
    monthlyPayment,
    extraPayment = 0,
  } = input;

  const monthlyRate = new Decimal(annualInterestRate).div(100).div(12);
  const totalPayment = new Decimal(monthlyPayment).add(extraPayment);

  let balance = new Decimal(principal);
  let totalInterest = new Decimal(0);
  const chartData: { month: number; balance: number }[] = [
    { month: 0, balance: principal },
  ];

  let month = 0;
  const maxMonths = 600; // 50 years cap

  while (balance.gt(0) && month < maxMonths) {
    month++;
    const interestDue = balance.mul(monthlyRate);
    totalInterest = totalInterest.add(interestDue);

    const amountDue = balance.add(interestDue);
    const actualPayment = Decimal.min(totalPayment, amountDue);
    const paymentToPrincipal = actualPayment.minus(interestDue);
    balance = balance.minus(paymentToPrincipal);

    chartData.push({
      month,
      balance: roundCurrency(Decimal.max(0, balance)),
    });
  }

  const payoffMonth = month;
  const totalInterestPaid = roundCurrency(totalInterest);
  const totalPaid = roundCurrency(
    new Decimal(principal).add(totalInterest)
  );
  const payoffYears = Math.round(payoffMonth * 10) / 120; // months to years

  let withoutExtra: DebtPayoffResult["withoutExtra"] | undefined;
  if (extraPayment > 0) {
    const base = calculateDebtPayoff({
      principal,
      annualInterestRate,
      monthlyPayment,
      extraPayment: 0,
    });
    withoutExtra = {
      payoffMonth: base.payoffMonth,
      totalInterestPaid: base.totalInterestPaid,
      payoffYears: base.payoffYears,
    };
  }

  return {
    payoffMonth,
    totalInterestPaid,
    totalPaid,
    payoffYears,
    chartData,
    withoutExtra,
  };
}

// --- Multi-debt types and simulation ---

export interface Debt {
  id: string;
  name: string;
  balance: number;
  apr: number;
  minimumPayment: number;
}

export type DebtStrategy = "snowball" | "avalanche";

export interface MultiDebtInput {
  debts: Debt[];
  strategy: DebtStrategy;
  extraPayment?: number;
}

export interface AmortizationRow {
  month: number;
  balance: number;
  interest: number;
  principal: number;
}

export interface MultiDebtStrategyResult {
  payoffMonth: number;
  totalInterestPaid: number;
  totalPaid: number;
  payoffYears: number;
  chartData: { month: number; balance: number }[];
  debtPayoffOrder: string[];
  amortizationData: AmortizationRow[];
}

export interface MultiDebtResult {
  snowball: MultiDebtStrategyResult;
  avalanche: MultiDebtStrategyResult;
}

function sortByStrategy(debts: Debt[], strategy: DebtStrategy): Debt[] {
  const copy = [...debts];
  if (strategy === "snowball") {
    return copy.sort((a, b) => {
      if (a.balance !== b.balance) return a.balance - b.balance;
      return b.apr - a.apr; // tie-break: higher APR first
    });
  }
  // avalanche: highest APR first
  return copy.sort((a, b) => {
    if (a.apr !== b.apr) return b.apr - a.apr;
    return a.balance - b.balance; // tie-break: smaller balance first
  });
}

/**
 * Simulate multi-debt payoff with roll-over. When a debt is paid off,
 * its minimum payment + extra rolls to the next debt in the strategy order.
 */
function simulateStrategy(
  debts: Debt[],
  strategy: DebtStrategy,
  extraPayment: number
): MultiDebtStrategyResult {
  const ordered = sortByStrategy(debts, strategy);
  const debtPayoffOrder: string[] = ordered.map((d) => d.id);

  const balances = new Map<string, Decimal>();
  const monthlyRates = new Map<string, Decimal>();
  const minimums = new Map<string, Decimal>();

  for (const d of ordered) {
    balances.set(d.id, new Decimal(d.balance));
    monthlyRates.set(d.id, new Decimal(d.apr).div(100).div(12));
    minimums.set(d.id, new Decimal(d.minimumPayment));
  }

  let totalInterest = new Decimal(0);
  const chartData: { month: number; balance: number }[] = [];
  const amortizationData: AmortizationRow[] = [];
  let totalBalance = ordered.reduce((sum, d) => sum + d.balance, 0);
  chartData.push({ month: 0, balance: totalBalance });

  let activeIndex = 0;
  let rollover = new Decimal(0); // Sum of minimums from paid-off debts + extra
  let month = 0;
  const maxMonths = 600;

  while (activeIndex < ordered.length && month < maxMonths) {
    month++;
    let monthInterest = new Decimal(0);
    let monthPrincipal = new Decimal(0);

    for (let i = 0; i < ordered.length; i++) {
      const id = ordered[i].id;
      const bal = balances.get(id)!;
      if (bal.lte(0)) continue;

      const rate = monthlyRates.get(id)!;
      const min = minimums.get(id)!;
      const interestDue = bal.mul(rate);
      totalInterest = totalInterest.add(interestDue);
      monthInterest = monthInterest.add(interestDue);

      const isActive = i === activeIndex;
      const paymentForThis = isActive
        ? min.add(rollover).add(extraPayment)
        : Decimal.min(min, bal.add(interestDue));
      const amountDue = bal.add(interestDue);
      const actualPayment = Decimal.min(paymentForThis, amountDue);
      const toPrincipal = Decimal.max(0, actualPayment.minus(interestDue));
      monthPrincipal = monthPrincipal.add(toPrincipal);
      const newBalance = bal.minus(toPrincipal);
      balances.set(id, Decimal.max(0, newBalance));

      if (isActive) {
        if (newBalance.lte(0)) {
          const leftover = paymentForThis.minus(actualPayment);
          rollover = min.add(leftover);
          activeIndex++;
        } else {
          rollover = new Decimal(0);
        }
      }
    }

    totalBalance = [...balances.values()].reduce(
      (sum, b) => sum + roundCurrency(b),
      0
    );
    chartData.push({ month, balance: totalBalance });
    amortizationData.push({
      month,
      balance: totalBalance,
      interest: roundCurrency(monthInterest),
      principal: roundCurrency(monthPrincipal),
    });
  }

  const payoffMonth = month;
  const totalPrincipal = debts.reduce((sum, d) => sum + d.balance, 0);
  const totalInterestPaid = roundCurrency(totalInterest);
  const totalPaid = roundCurrency(
    new Decimal(totalPrincipal).add(totalInterest)
  );
  const payoffYears = Math.round(payoffMonth * 10) / 120;

  return {
    payoffMonth,
    totalInterestPaid,
    totalPaid,
    payoffYears,
    chartData,
    debtPayoffOrder,
    amortizationData,
  };
}

/**
 * Returns debts whose minimum payment does not cover monthly interest (balance would grow).
 */
export function getMinimumPaymentWarnings(debts: Debt[]): Debt[] {
  return debts.filter((d) => {
    if (d.balance <= 0) return false;
    const monthlyInterest = d.balance * (d.apr / 100 / 12);
    return d.minimumPayment < monthlyInterest;
  });
}

/**
 * Calculate multi-debt payoff for both snowball and avalanche strategies.
 */
export function calculateMultiDebtPayoff(
  input: MultiDebtInput
): MultiDebtResult {
  const { debts, extraPayment = 0 } = input;
  const validDebts = debts.filter((d) => d.balance > 0);
  if (validDebts.length === 0) {
    const empty: MultiDebtStrategyResult = {
      payoffMonth: 0,
      totalInterestPaid: 0,
      totalPaid: 0,
      payoffYears: 0,
      chartData: [{ month: 0, balance: 0 }],
      debtPayoffOrder: [],
      amortizationData: [],
    };
    return { snowball: empty, avalanche: empty };
  }
  return {
    snowball: simulateStrategy(validDebts, "snowball", extraPayment),
    avalanche: simulateStrategy(validDebts, "avalanche", extraPayment),
  };
}
